// Que le pasa al cobro de una suscripcion, en palabras de operador.
//
// Una sola funcion para la lista general (/cobros) y para la ficha del
// cliente: las dos tienen que decir lo mismo de la misma suscripcion. Espeja
// las categorias del backend (libs/db/queries/pending-charges.ts) y el
// criterio del scheduler: rechazo del emisor (la tarjeta) vs error tecnico
// (el procesador). Confundirlos en pantalla lleva al operador a la accion
// equivocada — llamar al cliente por un problema que es nuestro, o al revés.

import type { PendingChargeCategory } from './api';

export type ChargeTone = 'error' | 'warning' | 'neutral';

export interface ChargeSituation {
  category: PendingChargeCategory;
  tone: ChargeTone;
  /** Una linea: que pasa. */
  title: string;
  /** Por que, con el motivo del banco o del procesador. */
  detail: string | null;
  /** Que va a pasar si nadie hace nada. */
  next: string;
}

/** Lo minimo que hace falta de una suscripcion (sirve para las dos fuentes). */
export interface ChargeFacts {
  status: string;
  payment_provider: string;
  auto_renew?: boolean;
  has_token?: boolean;
  current_period_end: string | null;
  next_retry_at: string | null;
  retry_count: number;
  charge_error_count: number;
  last_charge_error_code: string | null;
  last_charge_error_message: string | null;
  /** Motivo del ultimo rechazo del banco (payments.failure_code). */
  last_failure_code?: string | null;
  /** Si ya viene clasificada del backend, se respeta. */
  category?: PendingChargeCategory;
}

/** Vencida sin intento hace mas que esto = el cobrador no la esta tomando. */
const OVERDUE_MS = 2 * 3600 * 1000;

const DECLINE_LABEL: Record<string, string> = {
  insufficient_funds: 'fondos insuficientes',
  card_expired: 'tarjeta vencida',
  card_declined: 'el banco rechazó la operación',
  invalid_card: 'tarjeta inválida',
  limit_exceeded: 'límite excedido',
  card_retention: 'el banco pidió retener la tarjeta',
  incorrect_pin: 'código de seguridad incorrecto',
  // Codigo viejo (antes de 2026-09-28 se contaba como rechazo): era un error
  // de configuracion del comercio, no de la tarjeta.
  line_3_comercio_invalido: 'comercio inválido (error de configuración nuestro, no de la tarjeta)',
};

const TECH_LABEL: Record<string, string> = {
  provider_unavailable: 'el procesador o el banco no respondieron',
  invalid_request: 'la configuración del medio de pago no es válida',
  authentication_failed: 'nuestras credenciales con el procesador fallaron',
  unknown: 'el procesador respondió algo inesperado',
};

// Codigos de Line que el Anexo II llama "denegada" pero no son de la tarjeta
// (ver mapLineDeclineCode en el adapter): el cliente no tiene la culpa y otra
// tarjeta de la misma marca va a fallar igual. -1 es el de Line cuando el
// comercio de la marca no esta dado de alta.
const LINE_MERCHANT_CODES = new Set([-1, 3, 56, 57, 58, 89]);
const LINE_SYSTEM_CODES = new Set([12, 13, 30, 49, 94]);

// Correcciones contables de un cobro que SI ocurrio pero era de otra cuenta
// (libs/db/queries/payment-corrections.ts). No son rechazos: el pago queda
// `failed` en esta cuenta porque el cobro vive en otra fila o se devuelve.
const REASSIGNED_PREFIX = 'reassigned_to:';
const REFUND_PENDING = 'refund_pending';

export function declineLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  if (DECLINE_LABEL[code]) return DECLINE_LABEL[code];
  if (code === REFUND_PENDING) {
    return 'cobro anulado: lo hizo otra persona y se le reintegra (no es un rechazo)';
  }
  if (code.startsWith(REASSIGNED_PREFIX)) {
    return `cobro reasignado a la cuenta de quien lo pagó, como ${code.slice(REASSIGNED_PREFIX.length)} (no es un rechazo)`;
  }
  // `line_<codigo>_<mensaje>`: lo que dijo Line, tal cual, con el codigo.
  const m = /^line_(-?\d+)(?:_(.+))?$/.exec(code);
  if (m) {
    const n = Number(m[1]);
    const msg = m[2] ? m[2].replace(/_/g, ' ') : 'rechazado';
    if (LINE_MERCHANT_CODES.has(n)) {
      return `${msg} (código ${n} de Line: configuración del comercio, no de la tarjeta)`;
    }
    if (LINE_SYSTEM_CODES.has(n)) {
      return `${msg} (código ${n} de Line: error de formato o del sistema, no de la tarjeta)`;
    }
    return `${msg} (código ${n} de Line)`;
  }
  if (code === 'line_rejected') return 'Line rechazó el pago sin informar el motivo';
  return code.replace(/_/g, ' ');
}

export function technicalLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  return TECH_LABEL[code] ?? code.replace(/_/g, ' ');
}

function when(iso: string | null): string {
  if (!iso) return 'sin fecha';
  return new Date(iso).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * null = no hay un cobro pendiente (al dia, sin renovacion automatica,
 * cancelada, o la cobra la pasarela sola).
 */
export function chargeSituation(f: ChargeFacts, now: number = Date.now()): ChargeSituation | null {
  if (f.payment_provider !== 'line') return null;
  if (f.auto_renew === false || f.has_token === false) return null;

  const category = f.category ?? inferCategory(f, now);
  if (!category) return null;

  switch (category) {
    case 'blocked':
      return {
        category,
        tone: 'error',
        title: 'No se está cobrando: falla el procesador de pagos',
        detail:
          [technicalLabel(f.last_charge_error_code), f.last_charge_error_message]
            .filter(Boolean)
            .join(' — ') || null,
        next: f.next_retry_at
          ? `El sistema lo reintenta solo el ${when(f.next_retry_at)}. La tarjeta no tiene la culpa: no se da de baja.`
          : 'El sistema lo reintenta en la próxima corrida. No se da de baja.',
      };
    case 'overdue':
      return {
        category,
        tone: 'error',
        title: 'Venció y todavía no hubo ningún intento de cobro',
        detail: f.current_period_end ? `Venció el ${when(f.current_period_end)}.` : null,
        next: 'El cobrador debería haberla tomado. Reintentá a mano y avisá a soporte técnico.',
      };
    case 'grace': {
      const reason = declineLabel(f.last_failure_code);
      return {
        category,
        tone: 'warning',
        title: 'El banco rechazó el cobro',
        detail: reason ? `Motivo: ${reason}.` : null,
        next: f.next_retry_at
          ? `Reintento automático el ${when(f.next_retry_at)} (lleva ${f.retry_count} rechazo${f.retry_count === 1 ? '' : 's'}). Si se agotan, se da de baja.`
          : 'Tiene reintentos pendientes. Si se agotan, se da de baja.',
      };
    }
    case 'expired': {
      const reason = declineLabel(f.last_failure_code);
      return {
        category,
        tone: 'neutral',
        title: 'Se dio de baja por falta de pago',
        detail: reason ? `Último motivo: ${reason}.` : null,
        next: 'El sistema ya no la reintenta. Si el cobro pasa, se reactiva con un período que empieza hoy.',
      };
    }
  }
}

function inferCategory(f: ChargeFacts, now: number): PendingChargeCategory | null {
  const live = f.status === 'active' || f.status === 'grace_period';
  if (live && f.charge_error_count > 0) return 'blocked';
  if (f.status === 'grace_period') return 'grace';
  if (
    f.status === 'active' &&
    f.next_retry_at === null &&
    f.current_period_end !== null &&
    now - new Date(f.current_period_end).getTime() > OVERDUE_MS
  ) {
    return 'overdue';
  }
  // Una expirada solo cuenta si la baja fue por un cobro rechazado. La ficha
  // lo sabe por el ultimo pago fallido; sin ese dato no se afirma nada.
  if (f.status === 'expired' && f.last_failure_code) return 'expired';
  return null;
}

/**
 * Situaciones de la pantalla /cobros: las cuatro de un cobro no realizado más
 * el cobro duplicado (se cobró de más y hay que reintegrarlo), que viene en una
 * lista aparte del backend porque no es una suscripción a cobrar.
 */
export type ChargeListSituation = PendingChargeCategory | 'duplicate';

export const CATEGORY_LABEL: Record<ChargeListSituation, string> = {
  duplicate: 'Cobro duplicado — a reintegrar',
  blocked: 'Frenados por el procesador',
  overdue: 'Vencidos sin intento',
  grace: 'Rechazados, con reintento',
  expired: 'Dados de baja por falta de pago',
};

export const CATEGORY_HINT: Record<ChargeListSituation, string> = {
  duplicate: 'Se cobró de más y el pago no se acreditó. Hay que devolverlo.',
  blocked: 'Falla nuestra o del procesador. El cliente está al día.',
  overdue: 'El cobrador no los tomó. No debería pasar.',
  grace: 'La tarjeta no pasó. El sistema reintenta solo.',
  expired: 'Agotaron los reintentos en los últimos 45 días.',
};

/**
 * Contador del menú: cuántas cosas hay en /cobros y si alguna es urgente (algo
 * que el sistema no va a resolver solo: procesador fallando, vencida sin
 * intento, o plata cobrada de más). null = nada que mostrar. Tolera un backend
 * que todavía no informa `duplicate`.
 */
export function pendingChargesBadge(
  totals: Record<PendingChargeCategory, number> & { duplicate?: number },
): { total: number; urgent: boolean } | null {
  const duplicate = totals.duplicate ?? 0;
  const total = totals.blocked + totals.overdue + totals.grace + totals.expired + duplicate;
  if (total === 0) return null;
  return { total, urgent: totals.blocked + totals.overdue + duplicate > 0 };
}
